/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;
    /** @type {HTMLFormElement | null} */
    this.babyAgeForm = null;
    /** @type {(() => void) | undefined} */
    this.babyAgeSubmitHandler = undefined;
    /** @type {HTMLSelectElement | null} */
    this.babyAgeSelectEl = null;
    /** @type {HTMLElement | null} */
    this.babyAgeSelectWrap = null;
    /** @type {(() => void) | undefined} */
    this.babyAgeSelectOpen = undefined;
    /** @type {(() => void) | undefined} */
    this.babyAgeSelectClose = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector('.email-signup__button--integrated');
    this.input = this.querySelector('.email-signup__input');
    this.inputGroup = this.querySelector('.email-signup__input-group');

    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    this.bindBabyAgeTagsMerge();
    this.bindBabyAgeSelectChevron();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.babyAgeForm && this.babyAgeSubmitHandler) {
      this.babyAgeForm.removeEventListener('submit', this.babyAgeSubmitHandler);
      this.babyAgeForm = null;
      this.babyAgeSubmitHandler = undefined;
    }
    this.unbindBabyAgeSelectChevron();
  }

  /**
   * Junta `contact[tags]` (tags base + valor handle do select de idade) antes do envio.
   */
  bindBabyAgeTagsMerge() {
    const form = this.querySelector('form.email-signup__form');
    const tagsField = this.querySelector('[data-email-signup-tags-merge]');
    const ageSelect = this.querySelector('[data-email-signup-baby-age]');
    if (!form || !tagsField || !ageSelect) {
      return;
    }

    this.babyAgeForm = form;
    this.babyAgeSubmitHandler = () => {
      const baseRaw = tagsField.getAttribute('data-tags-base') || '';
      const base = baseRaw
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      const slug = (ageSelect.value || '').trim();
      const merged = slug.length > 0 ? [...base, slug] : base;
      tagsField.value = merged.join(', ');
    };
    form.addEventListener('submit', this.babyAgeSubmitHandler);
  }

  /**
   * Seta do select (custom): abre com foco/clique e fecha em blur/change.
   */
  bindBabyAgeSelectChevron() {
    const wrap = this.querySelector('[data-email-signup-select-wrap]');
    const sel = this.querySelector('[data-email-signup-baby-age]');
    if (!wrap || !sel) {
      return;
    }

    this.babyAgeSelectWrap = /** @type {HTMLElement} */ (wrap);
    this.babyAgeSelectEl = /** @type {HTMLSelectElement} */ (sel);

    this.babyAgeSelectOpen = () => {
      this.babyAgeSelectWrap?.classList.add('is-select-open');
    };

    this.babyAgeSelectClose = () => {
      this.babyAgeSelectWrap?.classList.remove('is-select-open');
    };

    sel.addEventListener('mousedown', this.babyAgeSelectOpen);
    sel.addEventListener('focus', this.babyAgeSelectOpen);
    sel.addEventListener('blur', this.babyAgeSelectClose);
    sel.addEventListener('change', this.babyAgeSelectClose);
  }

  unbindBabyAgeSelectChevron() {
    const sel = this.babyAgeSelectEl;
    if (!sel || !this.babyAgeSelectOpen || !this.babyAgeSelectClose) {
      this.babyAgeSelectEl = null;
      this.babyAgeSelectWrap = null;
      this.babyAgeSelectOpen = undefined;
      this.babyAgeSelectClose = undefined;
      return;
    }

    sel.removeEventListener('mousedown', this.babyAgeSelectOpen);
    sel.removeEventListener('focus', this.babyAgeSelectOpen);
    sel.removeEventListener('blur', this.babyAgeSelectClose);
    sel.removeEventListener('change', this.babyAgeSelectClose);
    this.babyAgeSelectWrap?.classList.remove('is-select-open');
    this.babyAgeSelectEl = null;
    this.babyAgeSelectWrap = null;
    this.babyAgeSelectOpen = undefined;
    this.babyAgeSelectClose = undefined;
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
